<script setup>
// ============================================================================
// BonBoZ CRM — HAI mục riêng trong sidebar hội thoại, mỗi mục một dải dữ liệu
// có cùng XUẤT XỨ: Nguồn quảng cáo (Meta) / Hồ sơ Epoint (ERP). Một component
// nhưng render 2 AccordionItem — giữ 1 mục kéo thả để vết chạm vào
// ContactPanel.vue vẫn là một thẻ.
// THÔNG TIN CÁ NHÂN (tên, SĐT, giới tính, tuổi) nằm HẾT ở header contact
// (ContactInfo.vue) — gom một chỗ, sửa tại chỗ. Panel không lặp lại.
// Trường chưa thu thập được thì ẨN, không vẽ chỗ trống (nguyên tắc 3).
// Nhóm "hội thoại" (nhân viên phụ trách, label) KHÔNG hiện lại ở đây — nó đã
// nằm ngay trên màn hình chính của Chatwoot; nhóm này chỉ đổ vào kho CRM
// (crm/bonboz_crm/store.py) phục vụ thống kê.
//
// PHẠM VI VỚI EPOINT — CHỈ ĐỌC VÀ LIÊN KẾT (chốt 12/08/2026).
// Epoint không có API ghi. Panel KHÔNG tạo hồ sơ / lịch hẹn / ghi chú; khách
// chưa có hồ sơ thì panel nói thẳng và nhắc nhân viên tự tạo bên Epoint rồi
// quay lại liên kết. Đừng dựng lại form tạo ở đây nếu chưa có API ghi thật —
// bản trước từng làm vậy và nó chỉ ghi vào nhật ký rồi biến mất khi tải lại
// trang, tức là đường làm MẤT lead.
//
// ĐÂY LÀ TOÀN BỘ PHẦN CRM NẰM TRONG REPO CHATWOOT. Ngoài file này, phần CRM chỉ
// chạm vào code chung đúng 3 dòng (xem bonboz-crm/README.md mục "Điểm nối"):
//   - ContactPanel.vue  : 1 import + 1 thẻ component
//   - useUISettings.js  : 1 mục 'epoint_crm' trong danh sách sidebar
// Mọi thứ khác (giao diện, gọi API, liên kết) nằm trong file này và ở service
// riêng `bonboz-crm`. Mục đích: đổi CRM không đụng việc của đội nền tảng chat
// và đội AI chatbot.
//
// NGUYÊN TẮC HIỂN THỊ (rút ra từ dữ liệu thật, đừng đổi nếu chưa đọc lý do):
//  1. Thẻ chia làm BA nhóm loại trừ nhau, vì gộp lại là nói sai với khách.
//     Khách mẫu #4601 có 60 thẻ: 1 còn buổi & còn hạn, 23 hết buổi nhưng CÒN
//     hạn, 36 đã quá hạn. Bản đầu in "Thẻ còn hạn (24)" khiến nhân viên hứa
//     với khách những dịch vụ họ không còn.
//  2. Bất đối xứng niềm tin: bảng `cards` đáng tin nên được phép khẳng định
//     "không còn thẻ nào dùng được"; bảng `appointments` đang lỗi ETL (đứng từ
//     13/03/2026) nên rỗng KHÔNG có nghĩa là không có lịch — tuyệt đối không
//     bao giờ in "chưa có lịch hẹn".
//  3. Trường null thì bỏ khỏi DOM, không vẽ dấu "–". Rất nhiều khách có
//     last_visit/note null; mỗi placeholder là một dòng vô nghĩa phải đọc.
//  4. Fail-open khi số liệu khuyết: tong_buoi null thì xếp vào nhóm dùng được.
//     Giấu nhầm thẻ khách thật sự có tệ hơn là hiện một thẻ mơ hồ.
// ============================================================================
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const store = useStore();
// Accordion nằm TRONG component này (không ở ContactPanel.vue) để vết chạm vào
// code chung còn đúng 1 dòng.
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();
const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(props.contactId) || {});
const currentUser = useMapGetter('getCurrentUser');
// Doc hoi thoai dang mo TRUC TIEP (khong them prop) de vet cham vao ContactPanel
// van dung 1 the component.
const currentChat = useMapGetter('getSelectedChat');

// ad_id do doi chatbot phat hien va ghi vao custom_attributes. Uu tien hoi thoai
// (quang cao dan toi DUNG cuoc tro chuyen nay), roi moi den contact.
// HOP DONG voi doi chatbot: ghi khoa `fb_ad_id` (chuoi so cua Meta).
const adId = computed(() => {
  const c = currentChat.value?.custom_attributes || {};
  const k = contact.value.custom_attributes || {};
  return String(c.fb_ad_id || k.fb_ad_id || '').trim();
});

const phone = computed(() => contact.value.phone_number || '');
const linkedId = computed(
  () => contact.value.custom_attributes?.epoint_customer_id || null
);
// Nhân viên đã chủ động bỏ liên kết -> không tự dò lại theo SĐT nữa, nếu không
// panel hiện lại ngay đúng hồ sơ vừa bỏ, trông như nút không ăn.
const unlinked = computed(
  () => !!contact.value.custom_attributes?.epoint_khong_lien_ket
);

// ---------------------------------------- "ĐÃ TẠO TRÊN EPOINT, CHỜ ĐỒNG BỘ"
// Nhân viên tạo hồ sơ bên Epoint xong, nhưng bản sao 30 phút mới chép sang một
// lần nên panel chưa dò thấy. Không có cờ này thì suốt thời gian đó panel vẫn
// khẳng định "khách chưa có hồ sơ" — sai, và ca sau vào lại tạo hồ sơ TRÙNG.
// Lưu mốc thời gian (không phải true/false) để tự hết hiệu lực: nếu quá lâu vẫn
// không dò thấy thì lời hứa "đang chờ" thành nói dối, phải quay về trạng thái
// chưa có hồ sơ kèm gợi ý kiểm tra lại SĐT.
const daTaoLuc = computed(
  () => contact.value.custom_attributes?.epoint_da_tao_luc || ''
);
// 3 lượt đồng bộ (30 phút/lượt) — quá đó thì gần như chắc chắn SĐT bên Epoint
// khác số ở đây, chứ không phải chờ chưa tới lượt.
const CHO_TOI_DA_PHUT = 90;

const daTaoTruocDay = computed(() => {
  if (!daTaoLuc.value) return null;
  const luc = new Date(daTaoLuc.value);
  return Number.isNaN(luc.getTime()) ? null : luc;
});
const gioDaTao = computed(() =>
  daTaoTruocDay.value
    ? daTaoTruocDay.value.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''
);
const phutDaCho = computed(() =>
  daTaoTruocDay.value
    ? Math.floor((Date.now() - daTaoTruocDay.value.getTime()) / 60000)
    : 0
);
const choDongBo = computed(
  () => !!daTaoTruocDay.value && phutDaCho.value < CHO_TOI_DA_PHUT
);
const choQuaHan = computed(
  () => !!daTaoTruocDay.value && phutDaCho.value >= CHO_TOI_DA_PHUT
);

// Liên kết do panel TỰ nối theo SĐT (không phải nhân viên bấm xác nhận).
// Không chặn đường đi, chỉ để hiện dòng ghi rõ xuất xứ + nút gỡ một chạm.
const tuDongLienKet = computed(
  () => !!contact.value.custom_attributes?.epoint_lien_ket_tu_dong
);

const loading = ref(false);
const refreshing = ref(false);
const saving = ref(false);
const error = ref('');
// Hồ sơ 360° từ kho CRM (nguồn quảng cáo, nhân khẩu học, hội thoại, Callio).
// Khác `summary`: cái kia là dữ liệu Epoint, cái này là dữ liệu của riêng ta.
const khach = ref(null);
// Nhóm "Nguồn quảng cáo" — kho CRM làm giàu thêm dịch vụ / bài viết từ bảng
// ánh xạ marketing khai. Chưa có kho thì vẫn hiện được mã quảng cáo.
const summary = ref(null);
// Contact mà `summary` đang thuộc về. Dùng để watch không tải lại ngay sau khi
// tự liên kết: lúc đó linkedId vừa đổi nhưng dữ liệu trên màn hình đã đúng rồi,
// tải lại chỉ làm panel nháy về spinner rồi hiện lại y nguyên.
const summaryCuaContact = ref(null);
const candidates = ref([]);
const showAllNotes = ref(false);
const showUsedCards = ref(false);
const showExpiredCards = ref(false);
// Lượt ETL gần nhất của bản sao Epoint, để nói được GIỜ CỤ THỂ nên quay lại dò.
const syncInfo = ref(null);

// Mỗi lần đổi contact / bấm làm mới sẽ tăng token. Response của request cũ về
// muộn sẽ bị bỏ qua -> không ghi đè dữ liệu của khách đang xem.
let requestToken = 0;

const DAY = 86400000;

// Tiêu đề nhóm CON bên trong một accordion (Lịch hẹn / Ghi chú / Thẻ). Nặng hơn
// nội dung MỘT bậc; ở cột 288px không đủ chỗ cho đường kẻ hay khoảng trắng lớn
// nên chênh lệch chữ là tín hiệu phân nhóm duy nhất còn dùng được.
// (Tiêu đề nhóm LỚN nay là tiêu đề của chính accordion, không cần class riêng.)
// Tieu de nhom: nho hon chu thuong nhung dam hon - phan cap bang co+can
// nang, KHONG viet hoa toan bo (chu Viet co dau in hoa nhu Ổ/Ê xau va kho
// doc, day la ly do bo kieu 'eyebrow uppercase' quen dung).
const sectionClass = 'text-xs font-semibold text-n-slate-11';

// <button> trần trong panel không có vòng focus nào — bàn phím bấm tới nơi mà
// không thấy đang ở đâu.
const focusRing =
  'rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/40';

const fmtMoney = value =>
  value == null ? '' : new Intl.NumberFormat('vi-VN').format(value) + 'đ';

const fmtDate = value => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}` : value;
};

const daysLeft = card =>
  card.het_han ? Math.ceil((new Date(card.het_han) - Date.now()) / DAY) : null;

// 'BB_Cắt mí trên' -> 'Cắt mí trên'. Tiền tố là mã nội bộ Epoint, lặp trên mọi
// dòng và ăn mất bề ngang quý giá của sidebar 320px.
const cardName = card =>
  String(card.ten_the || '').replace(
    /^(BB_|C\d+\.[A-Z]{2}_[A-Z0-9_]+_?)/,
    ''
  ) || card.ten_the;

const remaining = card => (card.tong_buoi ?? 0) - (card.da_dung ?? 0);

// Ba nhóm loại trừ nhau. KHÔNG gộp "hết buổi" vào "hết hạn": khách mẫu có 23 thẻ
// hết buổi nhưng CÒN hạn — gọi chung là "hết hạn" là nói sai với khách.
const hasSessionsLeft = card => {
  if (card.loai === 'money') return (card.money_con ?? 0) > 0;
  if (card.tong_buoi == null) return true; // fail-open, xem nguyên tắc 4
  return remaining(card) > 0;
};
const isUsable = card => !card.da_het_han && hasSessionsLeft(card);

const cardWarning = card => {
  if (card.is_reserve) return 'đang bảo lưu — cần mở tại quầy';
  const left = daysLeft(card);
  if (left != null && left <= 30) {
    return left < 0
      ? `đã quá hạn ${fmtDate(card.het_han)}`
      : `hết hạn ${fmtDate(card.het_han)} · còn ${left} ngày`;
  }
  return '';
};

const cust = computed(() => summary.value?.customer || null);
// Lấy THẲNG từ Epoint chứ không dùng thuộc tính đã đồng bộ: số ở đây luôn mới
// hơn, và không phụ thuộc job sync có chạy hay không.
const tongChi = computed(() => summary.value?.totals?.tong_chi ?? null);
const allCards = computed(() => summary.value?.cards || []);

const usableCards = computed(() =>
  [...allCards.value].filter(isUsable).sort((a, b) => {
    const la = daysLeft(a) ?? 9999;
    const lb = daysLeft(b) ?? 9999;
    if (la <= 30 || lb <= 30) return la - lb; // sắp hết hạn lên đầu
    if (a.loai === 'money') return -1;
    if (b.loai === 'money') return 1;
    return remaining(b) - remaining(a);
  })
);
const expiredCards = computed(() => allCards.value.filter(c => c.da_het_han));
// het buoi nhung VAN con han - khac han nhom tren
const usedUpCards = computed(() =>
  allCards.value.filter(c => !c.da_het_han && !hasSessionsLeft(c))
);
const expiringCount = computed(
  () => usableCards.value.filter(c => (daysLeft(c) ?? 9999) <= 30).length
);

const appointments = computed(() => summary.value?.appointments || []);
const notes = computed(() => summary.value?.notes || []);

const NOTES_PREVIEW = 3;
const visibleNotes = computed(() =>
  showAllNotes.value ? notes.value : notes.value.slice(0, NOTES_PREVIEW)
);

// line-clamp-2 o cot 288px chua duoc khoang 85 ky tu. Ghi chu o day trung binh
// 156 ky tu, dai nhat 1091 - tuc phan lon BI CAT. Khong the do duoc chieu cao
// that bang CSS nen uoc luong theo do dai, du de quyet dinh co hien nut hay khong.
const CLAMP_CHARS = 80;
const isLongNote = note => String(note.note || '').length > CLAMP_CHARS;

const expandedNotes = ref(new Set());
const toggleNote = index => {
  const next = new Set(expandedNotes.value);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  expandedNotes.value = next; // gan lai de Vue nhan ra thay doi
};

// ---------------------------------------------------- NHÓM "THÔNG TIN KHÁCH"
// Danh tính (tên/SĐT/giới tính/tuổi) chỉ lấy từ Epoint khi ĐÃ liên kết — chưa
// liên kết thì dùng dữ liệu của contact Chatwoot. Liên kết nay chạy tự động khi
// SĐT khớp đúng một hồ sơ, nên thực tế nhánh "chưa liên kết" chỉ còn xảy ra khi
// nhân viên chủ động gỡ, hoặc khi nhiều hồ sơ trùng số và họ chưa chọn.
// Tuổi/giới tính từ chat do đội chatbot/nhân viên ghi vào custom_attributes
// theo hợp đồng khoá `kh_tuoi` / `kh_gioi_tinh` — chưa có thì ẨN dòng đó.
const GENDER_LABELS = { female: 'Nữ', male: 'Nam', other: 'Khác' };

const confirmedCust = computed(() => (linkedId.value ? cust.value : null));

// Tuổi nhân viên đã ghi nhận (header sửa) — chỉ dùng ở bảng dữ liệu bày sẵn để
// copy sang Epoint. Panel không hiện lại: header đã có.
const soTuoi = computed(() => contact.value.custom_attributes?.kh_tuoi || '');
const hoTen = computed(
  () => confirmedCust.value?.full_name || contact.value.name || ''
);
// Tên trên Facebook thường khác tên thật trong hồ sơ — chỉ hiện khi KHÁC,
// trùng nhau mà in hai dòng là lặp vô nghĩa.
const gioiTinh = computed(() => {
  const g =
    confirmedCust.value?.gender ||
    contact.value.custom_attributes?.kh_gioi_tinh ||
    '';
  return GENDER_LABELS[g] || '';
});

// Bản sao Epoint chạy ETL mỗi 30 phút (đã đo: khởi động phút :00 và :30, mỗi
// lượt ~3 phút). Từ mốc lượt gần nhất suy ra lượt kế tiếp — nói giờ cụ thể để
// nhân viên biết khi nào quay lại, thay vì "chờ 30 phút" rồi tự đoán.
const CHU_KY_PHUT = 30;
const CHAY_MAT_PHUT = 4;

const gioDongBoToi = computed(() => {
  const moc = syncInfo.value?.started_at;
  if (!moc) return '';
  const batDau = new Date(moc);
  if (Number.isNaN(batDau.getTime())) return '';
  // Lượt kế tiếp sau THỜI ĐIỂM HIỆN TẠI: cộng dồn chu kỳ cho tới khi vượt qua
  // bây giờ (mốc trả về có thể đã cũ nếu panel mở lâu).
  const xong = new Date(batDau.getTime() + CHAY_MAT_PHUT * 60000);
  while (xong.getTime() <= Date.now()) {
    xong.setTime(xong.getTime() + CHU_KY_PHUT * 60000);
  }
  return xong.toLocaleTimeString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
  });
});

// Dev chạy trực tiếp localhost:3000 -> service ở cổng 8088.
// Production: service sau reverse proxy cùng domain, đường dẫn /crm.
const apiBase = ['localhost', '127.0.0.1'].includes(window.location.hostname)
  ? 'http://localhost:8088'
  : '/crm';

const REQUEST_TIMEOUT = 15000;

const request = async (path, body = null) => {
  const token = currentUser.value?.access_token;
  if (!token) throw new Error('Thiếu quyền API — bật API access cho tài khoản');

  // Không có timeout thì panel treo vĩnh viễn khi service/Epoint không trả lời.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT);
  try {
    const res = await fetch(`${apiBase}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: {
        'X-Chatwoot-Token': token,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: abort.signal,
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Dịch vụ CRM trả lỗi ${res.status}`);
    }
    return res.json();
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('Epoint phản hồi quá lâu');
    throw e;
  } finally {
    clearTimeout(timer);
  }
};

// Kho CRM luôn tải được, không phụ thuộc Epoint: khách chưa có hồ sơ Epoint
// vẫn phải thấy nguồn quảng cáo và thông tin đã thu thập.
const loadKhach = async () => {
  khach.value = null;
  if (!props.contactId) return;
  try {
    khach.value = await request(`/api/khach/${props.contactId}`);
  } catch {
    // Chưa có dòng trong kho (404 -> null) hoặc service chưa chạy: panel vẫn
    // dùng được, chỉ mất phần làm giàu.
  }
};

const loadSummary = async (customerId, { fresh = false } = {}) => {
  requestToken += 1;
  const token = requestToken;
  loading.value = true;
  error.value = '';
  candidates.value = [];
  try {
    const data = await request(
      `/api/customer/${customerId}${fresh ? '?fresh=1' : ''}`
    );
    if (token !== requestToken) return;
    summary.value = data;
    summaryCuaContact.value = data ? props.contactId : null;
  } catch (e) {
    if (token !== requestToken) return;
    summary.value = null;
    summaryCuaContact.value = null;
    error.value = e.message;
  } finally {
    if (token === requestToken) loading.value = false;
  }
};

// Tai lai ho so mà KHONG bat co `loading`: co do lam ca panel thanh spinner,
// che mat hop ket qua vua hien sau khi nhan vien bam luu.
const refreshSummaryQuietly = async customerId => {
  requestToken += 1;
  const token = requestToken;
  try {
    const data = await request(`/api/customer/${customerId}?fresh=1`);
    if (token === requestToken && data) summary.value = data;
  } catch {
    // Ghi da thanh cong roi; tai lai that bai thi giu nguyen man hinh cu,
    // khong bao loi lam nhan vien tuong thao tac vua roi hong.
  }
};

const writeAttrs = async next => {
  saving.value = true;
  try {
    // Store action nhận camelCase `customAttributes` rồi tự đổi sang
    // custom_attributes (store/modules/contacts/actions.js:134). Truyền thẳng
    // snake_case sẽ rơi vào nhánh snakecaseKeys(deep) — chạy được nhưng là
    // đường vòng, và deep sẽ đổi tên khoá nếu sau này thêm khoá camelCase.
    await store.dispatch('contacts/update', {
      id: props.contactId,
      customAttributes: next,
    });
    return true;
  } catch (e) {
    useAlert(t('BONBOZ.CRM.SAVE_FAILED', { loi: e.message }));
    return false;
  } finally {
    saving.value = false;
  }
};

// QUAN TRONG — Chatwoot MERGE custom_attributes o phia server:
//   contacts_controller.rb:178  @contact.custom_attributes.merge(params[...])
// Nghia la XOA khoa o phia trinh duyet roi gui len KHONG xoa duoc gi: khoa cu
// van song sau khi merge. Muon bo mot co thi phai GHI DE bang gia tri falsy.
// (Chatwoot co endpoint destroy_custom_attributes rieng, nhung dung no phai
// them mot lan goi mang nua - ghi de false don gian va du dung o day.)
const saveLink = async (
  customerId,
  { ngungDoLai = true, tuDong = false } = {}
) => {
  const next = {
    ...(contact.value.custom_attributes || {}),
    epoint_customer_id: customerId || null,
    // Co "dung tu do lai" de sync.py khong noi lai sau 15 phut, hoan tac im
    // lang thao tac cua nhan vien.
    // CHI dat khi nhan vien CHU DONG noi "khong phai khach nay". Voi lien ket
    // hong (ho so bi xoa/gop ben Epoint) thi nguoc lai - ho bam chinh la de do
    // lai, dat co nay len la tu chan dung viec vua hua tren nhan nut.
    epoint_khong_lien_ket: !customerId && ngungDoLai,
    epoint_lien_ket_hong: false,
    // Liên kết được rồi thì cờ "đang chờ đồng bộ" hết vai trò. Ghi đè bằng
    // chuỗi rỗng chứ không xoá khoá: Chatwoot MERGE custom_attributes ở server
    // nên bỏ khoá khỏi payload KHÔNG xoá được nó.
    epoint_da_tao_luc: customerId ? '' : daTaoLuc.value,
    epoint_lien_ket_tu_dong: !!customerId && tuDong,
  };
  const ok = await writeAttrs(next);
  // Tự liên kết chạy mỗi lần mở hội thoại -> báo toast là phiền, và nó không
  // phải hành động của nhân viên nên không có gì để xác nhận với họ.
  if (ok && !tuDong) {
    useAlert(customerId ? t('BONBOZ.CRM.LINKED') : t('BONBOZ.CRM.UNLINK_DONE'));
  }
};

const searchByPhone = async () => {
  requestToken += 1;
  const token = requestToken;
  loading.value = true;
  error.value = '';
  summary.value = null;
  candidates.value = [];
  try {
    // fresh=1: nút này tồn tại để HỎI LẠI, mà cache 60s phía service sẽ trả
    // nguyên kết quả "không thấy" cũ mà không hỏi Epoint — tức là chồng thêm
    // một lớp cũ nữa lên trên độ trễ 30 phút vốn đã không tránh được.
    const res = await request(
      `/api/search?phone=${encodeURIComponent(phone.value)}&fresh=1`
    );
    if (token !== requestToken) return;
    syncInfo.value = res?.dong_bo || null;
    const list = res?.results || [];
    if (list.length === 1) {
      // ĐÚNG MỘT hồ sơ trùng SĐT -> nối luôn, không bắt xác nhận.
      // Đã đo trên 6.823 hồ sơ Epoint: chỉ 2 số điện thoại bị dùng chung, và cả
      // hai đều là CÙNG một người bị nhập trùng ("Bảo Yến"/"Bác Yến") chứ không
      // phải hai người khác nhau. Nguy cơ nối nhầm vì dùng chung số ~ 0. Nhiều
      // hồ sơ trùng số thì vẫn rơi xuống màn hình chọn bên dưới.
      loading.value = false;
      await loadSummary(list[0].customer_id);
      if (summary.value) saveLink(list[0].customer_id, { tuDong: true });
      return;
    }
    candidates.value = list;
  } catch (e) {
    if (token !== requestToken) return;
    error.value = e.message;
  } finally {
    if (token === requestToken) loading.value = false;
  }
};

// Đang có hồ sơ trên màn hình thì tải lại ÂM THẦM: bật `loading` sẽ thay cả
// panel bằng spinner, nhân viên đang đọc dở ghi chú thì mất chỗ, rồi nội dung
// nhảy về. Vòng quay nằm gọn trong nút, phần còn lại đứng yên.
const refresh = async () => {
  if (refreshing.value || loading.value) return;
  const id = linkedId.value || summary.value?.customer?.customer_id;
  if (!id) {
    if (phone.value) searchByPhone();
    return;
  }
  if (!summary.value) {
    loadSummary(id, { fresh: true });
    return;
  }
  refreshing.value = true;
  try {
    await refreshSummaryQuietly(id);
  } finally {
    refreshing.value = false;
  }
};
// Nhân viên vừa tạo hồ sơ bên Epoint -> ghi mốc lại. Không tạo được gì bên
// Epoint từ đây, đây thuần tuý là ghi chú trạng thái để ca sau không tạo trùng.
const danhDauDaTao = async () => {
  if (
    await writeAttrs({
      ...(contact.value.custom_attributes || {}),
      epoint_da_tao_luc: new Date().toISOString(),
      epoint_khong_lien_ket: false,
    })
  ) {
    useAlert(t('BONBOZ.CRM.MARKED'));
  }
};

const boDanhDauDaTao = async () => {
  await writeAttrs({
    ...(contact.value.custom_attributes || {}),
    epoint_da_tao_luc: '',
  });
};

const clearUnlinkFlag = async () => {
  await writeAttrs({
    ...(contact.value.custom_attributes || {}),
    epoint_khong_lien_ket: false,
  });
};

// Nguồn sự thật: đã liên kết thì luôn tra theo id, chưa thì dò theo SĐT.
watch(
  [() => props.contactId, linkedId, phone, unlinked],
  () => {
    // Vừa tự liên kết xong: linkedId mới trỏ đúng hồ sơ đang hiển thị cho đúng
    // contact này -> không có gì để tải lại.
    if (
      linkedId.value &&
      summaryCuaContact.value === props.contactId &&
      summary.value?.customer?.customer_id === linkedId.value
    ) {
      return;
    }
    summary.value = null;
    summaryCuaContact.value = null;
    candidates.value = [];
    error.value = '';
    showAllNotes.value = false;
    expandedNotes.value = new Set();
    syncInfo.value = null;
    showUsedCards.value = false; // trạng thái gập reset khi đổi khách
    showExpiredCards.value = false;
    if (!props.contactId) return;
    loadKhach();
    if (linkedId.value) loadSummary(linkedId.value);
    // `unlinked` -> khong do lai gi ca: ton trong quyet dinh cua nhan vien
    else if (!unlinked.value && phone.value) searchByPhone();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- HAI MỤC RIÊNG, mỗi mục một DẢI THÔNG TIN có cùng xuất xứ:
           Nguồn quảng cáo ← Meta gửi khi khách bấm quảng cáo
           Hồ sơ Epoint    ← ERP của phòng khám
         Trước đây nhồi cả hai (cộng thông tin cá nhân) vào một accordion
         "Hồ sơ khách" nên gập/mở không giúp được gì. -->

    <!-- ══ HỒ SƠ EPOINT ══ thẻ dịch vụ, ghi chú, lịch hẹn, liên kết -->
    <AccordionItem
      :title="$t('BONBOZ.CRM.TITLE')"
      compact
      :is-open="!isContactSidebarItemOpen('is_epoint_crm_closed')"
      @toggle="() => toggleSidebarUIState('is_epoint_crm_closed')"
    >
      <div class="px-4 py-3 text-n-slate-12 flex flex-col gap-4">
        <div class="flex justify-end">
          <Button
            v-if="cust"
            icon="i-lucide-refresh-cw"
            size="xs"
            variant="ghost"
            color="slate"
            :is-loading="refreshing"
            :aria-label="$t('BONBOZ.CRM.REFRESH')"
            @click="refresh"
          />
        </div>

        <div v-if="loading" class="flex justify-center py-3" role="status">
          <Spinner size="20" class="text-n-brand" />
          <span class="sr-only">{{ $t('BONBOZ.CRM.LOADING') }}</span>
        </div>

        <div v-else-if="error" class="flex flex-col gap-2" role="alert">
          <p class="text-sm text-n-ruby-11">{{ $t('BONBOZ.CRM.ERROR') }}</p>
          <p class="text-xs text-n-slate-11">{{ error }}</p>
          <Button
            :label="$t('BONBOZ.CRM.RETRY')"
            size="xs"
            variant="faded"
            @click="refresh"
          />
        </div>

        <div v-else-if="unlinked" class="flex flex-col gap-2">
          <p class="text-sm text-n-slate-12">{{ $t('BONBOZ.CRM.UNLINKED') }}</p>
          <p class="text-xs text-n-slate-11">
            {{ $t('BONBOZ.CRM.UNLINKED_HINT') }}
          </p>
          <Button
            :label="$t('BONBOZ.CRM.SEARCH_BY_PHONE')"
            size="xs"
            variant="faded"
            :is-loading="saving"
            @click="clearUnlinkFlag"
          />
        </div>

        <div v-else-if="candidates.length > 1" class="flex flex-col gap-2">
          <p class="text-xs text-n-amber-11">
            {{ $t('BONBOZ.CRM.MANY_MATCHES', { n: candidates.length }) }}
          </p>
          <button
            v-for="c in candidates"
            :key="c.customer_id"
            class="w-full text-left px-2 py-1.5 rounded-md border border-n-weak hover:bg-n-alpha-2"
            :class="focusRing"
            @click="loadSummary(c.customer_id)"
          >
            <span class="text-sm text-n-slate-12">{{ c.full_name }}</span>
            <span class="block text-xs text-n-slate-11">
              {{ c.customer_code }}
            </span>
          </button>
        </div>

        <!-- CHUA CO HO SO EPOINT.
               Epoint khong co API ghi -> panel KHONG tao ho so. Viec dung la noi
               ro tinh trang roi nhac nhan vien tu tao ben Epoint. Bay san san
               thong tin de ho copy sang, va canh bao do tre cua ban sao 30 phut -
               thieu canh bao nay thi ho tao xong, quay lai khong thay, tuong hong. -->
        <div v-else-if="!cust" class="flex flex-col gap-3">
          <!-- Lien ket cu tro toi ho so khong con: viec dung la bo lien ket
                 hong roi do lai, khong phai tao moi. -->
          <template v-if="linkedId">
            <p class="text-sm text-n-slate-12">
              {{ $t('BONBOZ.CRM.LINK_INVALID') }}
            </p>
            <p class="text-xs text-n-amber-11">
              {{ $t('BONBOZ.CRM.LINK_INVALID_HINT', { id: linkedId }) }}
            </p>
            <Button
              :label="$t('BONBOZ.CRM.UNLINK_AND_SEARCH')"
              size="sm"
              variant="faded"
              icon="i-lucide-unlink"
              :is-loading="saving"
              @click="saveLink(null, { ngungDoLai: false })"
            />
          </template>

          <!-- DA TAO BEN EPOINT, DANG CHO BAN SAO CHEP SANG.
                 Khong co trang thai nay thi suot 30 phut cho, panel van khang dinh
                 "khach chua co ho so" - ca sau vao doc se tao ho so TRUNG. -->
          <template v-else-if="choDongBo">
            <p class="text-sm font-medium text-n-teal-11">
              <i class="i-lucide-hourglass" />
              {{ $t('BONBOZ.CRM.PENDING', { gio: gioDaTao }) }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                gioDongBoToi
                  ? $t('BONBOZ.CRM.PENDING_ETA', { gio: gioDongBoToi })
                  : $t('BONBOZ.CRM.PENDING_NO_ETA')
              }}
            </p>
            <p v-if="phone" class="text-xs text-n-slate-11">
              {{ $t('BONBOZ.CRM.PENDING_PHONE', { phone }) }}
            </p>
            <div class="flex gap-3">
              <Button
                :label="$t('BONBOZ.CRM.SEARCH_NOW')"
                size="xs"
                variant="faded"
                icon="i-lucide-refresh-cw"
                :disabled="!phone"
                :is-loading="loading"
                @click="searchByPhone"
              />
              <Button
                :label="$t('BONBOZ.CRM.UNMARK_LONG')"
                size="xs"
                variant="ghost"
                color="slate"
                :is-loading="saving"
                @click="boDanhDauDaTao"
              />
            </div>
          </template>

          <template v-else>
            <!-- Chua co SDT thi CHUA HE do lan nao - khong duoc khang dinh
                   khach "chua co ho so", vi khong biet. Noi dung viec dang can. -->
            <p class="text-sm font-medium text-n-amber-11">
              <i class="i-lucide-user-x" />
              {{
                phone
                  ? $t('BONBOZ.CRM.NO_PROFILE')
                  : $t('BONBOZ.CRM.NOT_SEARCHED')
              }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                phone
                  ? $t('BONBOZ.CRM.NO_PROFILE_HINT', { phone })
                  : $t('BONBOZ.CRM.NO_PHONE_HINT')
              }}
            </p>

            <div
              class="flex flex-col gap-2 p-2 rounded-md border border-n-weak"
            >
              <p class="text-xs font-medium text-n-slate-12">
                {{ $t('BONBOZ.CRM.TODO_TITLE') }}
              </p>
              <ol
                class="text-xs text-n-slate-11 list-decimal pl-4 flex flex-col gap-0.5"
              >
                <li>{{ $t('BONBOZ.CRM.TODO_1') }}</li>
                <li>{{ $t('BONBOZ.CRM.TODO_2') }}</li>
                <li>{{ $t('BONBOZ.CRM.TODO_3') }}</li>
              </ol>

              <!-- Bay san du lieu de khoi phai lat qua lat lai giua 2 tab -->
              <dl class="flex flex-col gap-1 text-xs pt-1">
                <div v-if="hoTen" class="flex gap-3">
                  <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                    {{ $t('BONBOZ.CRM.F_FULL_NAME') }}
                  </dt>
                  <dd class="min-w-0 flex-1 leading-5 text-n-slate-12 truncate">
                    {{ hoTen }}
                  </dd>
                </div>
                <div v-if="phone" class="flex gap-3">
                  <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                    {{ $t('BONBOZ.CRM.F_PHONE') }}
                  </dt>
                  <dd class="min-w-0 flex-1 leading-5 text-n-slate-12">
                    {{ phone }}
                  </dd>
                </div>
                <div v-if="gioiTinh" class="flex gap-3">
                  <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                    {{ $t('BONBOZ.CRM.F_GENDER') }}
                  </dt>
                  <dd class="min-w-0 flex-1 leading-5 text-n-slate-12">
                    {{ gioiTinh }}
                  </dd>
                </div>
                <div v-if="soTuoi" class="flex gap-3">
                  <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                    {{ $t('BONBOZ.CRM.F_AGE') }}
                  </dt>
                  <dd class="min-w-0 flex-1 leading-5 text-n-slate-12">
                    {{ soTuoi }}
                  </dd>
                </div>
                <div v-if="adId" class="flex gap-3">
                  <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                    {{ $t('BONBOZ.CRM.F_PROFILE_CODE') }}
                  </dt>
                  <dd
                    class="min-w-0 flex-1 leading-5 text-n-slate-12 break-all"
                  >
                    {{ adId }}
                  </dd>
                </div>
              </dl>
              <p v-if="adId" class="text-xs text-n-slate-11">
                {{ $t('BONBOZ.CRM.AD_ID_HINT') }}
              </p>
            </div>

            <!-- Danh dau da tao nhung qua 90 phut (3 luot dong bo) van khong
                   do thay -> khong con la "cho chua toi luot" nua. Noi thang
                   nguyen nhan kha di nhat thay vi de nhan vien cho tiep vo ich. -->
            <p v-if="choQuaHan" class="text-xs text-n-ruby-11">
              <i class="i-lucide-alert-triangle" />
              {{ $t('BONBOZ.CRM.OVERDUE', { gio: gioDaTao, phut: phutDaCho }) }}
            </p>

            <!-- Do tre nay KHONG tranh duoc: ta doc ban SAO cua Epoint, no
                   chep sang moi 30 phut. Bam do lai som hon chi doc lai dung ban
                   sao cu. Noi gio cu the de nhan vien khong ngoi bam lai vo ich. -->
            <p v-else class="text-xs text-n-amber-11">
              <i class="i-lucide-clock" />
              {{
                gioDongBoToi
                  ? $t('BONBOZ.CRM.SYNC_ETA', { gio: gioDongBoToi })
                  : $t('BONBOZ.CRM.SYNC_NO_ETA')
              }}
            </p>

            <div class="flex flex-col gap-2">
              <!-- Nut chinh: ghi lai viec nhan vien vua lam ben Epoint. Khong
                     tao gi ca - chi danh dau de panel doi sang trang thai cho. -->
              <Button
                :label="
                  choQuaHan
                    ? $t('BONBOZ.CRM.MARK_AGAIN')
                    : $t('BONBOZ.CRM.MARK_CREATED')
                "
                size="sm"
                variant="solid"
                color="teal"
                icon="i-lucide-check"
                :disabled="!phone"
                :is-loading="saving"
                @click="danhDauDaTao"
              />
              <p v-if="!phone" class="text-xs text-n-slate-11">
                {{ $t('BONBOZ.CRM.NEED_PHONE') }}
              </p>
              <div class="flex gap-3">
                <Button
                  :label="$t('BONBOZ.CRM.SEARCH_BY_PHONE')"
                  size="xs"
                  variant="faded"
                  icon="i-lucide-refresh-cw"
                  :disabled="!phone"
                  :is-loading="loading"
                  @click="searchByPhone"
                />
                <Button
                  v-if="choQuaHan"
                  :label="$t('BONBOZ.CRM.UNMARK')"
                  size="xs"
                  variant="ghost"
                  color="slate"
                  :is-loading="saving"
                  @click="boDanhDauDaTao"
                />
              </div>
            </div>
          </template>
        </div>

        <template v-else>
          <!-- TU LIEN KET theo SDT, khong bat xac nhan.
                 Do tren 6.823 ho so Epoint: chi 2 so dien thoai bi dung chung, ca
                 hai deu la CUNG mot nguoi bi nhap trung chu khong phai hai nguoi
                 khac nhau -> nguy co noi nham ~ 0, khong dang bat nhan vien bam
                 them mot nut moi lan mo hoi thoai. Van ghi ro xuat xu va de san
                 duong go mot cham cho truong hop hiem. -->
          <p
            v-if="tuDongLienKet"
            class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
          >
            <span class="truncate">
              <i class="i-lucide-link" />
              {{ $t('BONBOZ.CRM.AUTO_LINKED') }}
            </span>
            <button
              class="hover:text-n-ruby-11 hover:underline shrink-0 disabled:opacity-50"
              :class="focusRing"
              :disabled="saving"
              @click="saveLink(null)"
            >
              {{ $t('BONBOZ.CRM.NOT_THIS_CUSTOMER') }}
            </button>
          </p>

          <!-- Ho ten/SDT da len nhom "Thong tin khach" — o day chi con dinh danh
               Epoint, moi dong van theo dang "Ten truong: noi dung" -->
          <dl class="min-w-0 flex flex-col gap-1 text-sm">
            <!-- Ten tren Epoint phai hien: header contact o dau sidebar la ten
                 FACEBOOK, thuong la biet danh. Khong hien ten nay thi nhan vien
                 khong biet minh dang noi voi ho so nao. -->
            <div v-if="cust.full_name" class="flex gap-3">
              <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                {{ $t('BONBOZ.CRM.F_NAME') }}
              </dt>
              <dd
                class="min-w-0 flex-1 leading-5 text-n-slate-12 font-medium truncate"
              >
                {{ cust.full_name }}
              </dd>
            </div>
            <div v-if="cust.phone1" class="flex gap-3">
              <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                {{ $t('BONBOZ.CRM.F_PHONE') }}
              </dt>
              <dd class="min-w-0 flex-1 leading-5 text-n-slate-12 truncate">
                {{ cust.phone1 }}
              </dd>
            </div>
            <div class="flex gap-3">
              <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                {{ $t('BONBOZ.CRM.F_EPOINT_ID') }}
              </dt>
              <dd class="min-w-0 flex-1 leading-5 text-n-slate-12 truncate">
                {{ cust.customer_id }}
                <span v-if="cust.customer_code" class="text-n-slate-11">
                  {{ $t('BONBOZ.CRM.CODE', { ma: cust.customer_code }) }}
                </span>
              </dd>
            </div>
            <!-- Doc THANG tu Epoint, khong dung thuoc tinh da dong bo: so o day
                   luon moi hon, va khong phu thuoc job sync co chay hay khong. -->
            <div v-if="tongChi" class="flex gap-3">
              <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                {{ $t('BONBOZ.CRM.F_TOTAL_SPEND') }}
              </dt>
              <dd class="min-w-0 flex-1 leading-5 text-n-slate-12">
                {{ fmtMoney(tongChi) }}
              </dd>
            </div>
            <div v-if="cust.last_visit" class="flex gap-3">
              <dt class="w-24 shrink-0 text-xs leading-5 text-n-slate-11">
                {{ $t('BONBOZ.CRM.F_LAST_VISIT') }}
              </dt>
              <dd class="min-w-0 flex-1 leading-5 text-n-slate-12">
                {{ fmtDate(cust.last_visit) }}
              </dd>
            </div>
          </dl>

          <p v-if="expiringCount" class="text-xs text-n-amber-11">
            <i class="i-lucide-hourglass" />
            {{ $t('BONBOZ.CRM.EXPIRING', { n: expiringCount }) }}
          </p>

          <!-- Lịch hẹn: CHỈ hiện khi có. Bảng này đang lỗi ETL nên rỗng không có
               nghĩa là khách không có lịch — không được khẳng định điều ngược lại -->
          <div v-if="appointments.length" class="flex flex-col gap-2">
            <p :class="sectionClass">
              {{ $t('BONBOZ.CRM.APPOINTMENTS', { n: appointments.length }) }}
            </p>
            <p
              v-for="(a, i) in appointments"
              :key="i"
              class="text-xs px-1.5 py-0.5 ml-2 rounded bg-n-teal-5 text-n-teal-12 w-fit"
            >
              <i class="i-lucide-calendar-check" />
              {{ fmtDate(a.ngay) }}{{ a.gio ? ' · ' + a.gio : '' }}
            </p>
          </div>

          <div v-if="notes.length" class="flex flex-col gap-2">
            <p :class="sectionClass">
              {{ $t('BONBOZ.CRM.NOTES', { n: notes.length }) }}
            </p>
            <div v-for="(n, i) in visibleNotes" :key="i" class="pl-3">
              <p
                class="text-xs text-n-slate-12"
                :class="{ 'line-clamp-2': !expandedNotes.has(i) }"
                :title="n.note"
              >
                <span class="text-n-slate-11">{{ fmtDate(n.ngay) }}</span>
                {{ n.note }}
              </p>
              <button
                v-if="isLongNote(n)"
                class="text-xs text-n-slate-11 hover:underline"
                :class="focusRing"
                @click="toggleNote(i)"
              >
                {{
                  expandedNotes.has(i)
                    ? $t('BONBOZ.CRM.NOTE_COLLAPSE')
                    : $t('BONBOZ.CRM.NOTE_EXPAND')
                }}
              </button>
            </div>
            <button
              v-if="notes.length > NOTES_PREVIEW && !showAllNotes"
              class="text-xs text-n-slate-11 hover:underline text-left pl-3"
              :class="focusRing"
              @click="showAllNotes = true"
            >
              {{
                $t('BONBOZ.CRM.NOTES_MORE', { n: notes.length - NOTES_PREVIEW })
              }}
            </button>
          </div>

          <div class="flex flex-col gap-2">
            <p :class="sectionClass">
              {{ $t('BONBOZ.CRM.CARDS_USABLE', { n: usableCards.length }) }}
            </p>
            <p v-if="!usableCards.length" class="text-xs text-n-slate-11 pl-3">
              {{ $t('BONBOZ.CRM.CARDS_NONE') }}
            </p>
            <div
              v-for="(k, i) in usableCards"
              :key="i"
              class="flex flex-col gap-0.5 pl-3"
            >
              <div class="flex justify-between items-baseline gap-2">
                <span
                  class="text-sm text-n-slate-12 truncate"
                  :title="k.ten_the"
                >
                  {{ cardName(k) }}
                </span>
                <span
                  v-if="k.loai === 'money'"
                  class="text-sm text-n-slate-12 shrink-0"
                  :title="
                    $t('BONBOZ.CRM.CARD_MONEY', {
                      nap: fmtMoney(k.money),
                      dung: fmtMoney(k.money_using),
                    })
                  "
                >
                  {{ fmtMoney(k.money_con) }}
                </span>
                <span
                  v-else
                  class="text-xs px-1.5 rounded shrink-0 bg-n-teal-5 text-n-teal-12"
                >
                  {{
                    k.tong_buoi == null
                      ? $t('BONBOZ.CRM.CARD_USED', { n: k.da_dung ?? 0 })
                      : $t('BONBOZ.CRM.CARD_LEFT', {
                          con: remaining(k),
                          tong: k.tong_buoi,
                        })
                  }}
                </span>
              </div>
              <span v-if="cardWarning(k)" class="text-xs text-n-amber-11">
                {{ cardWarning(k) }}
              </span>
            </div>
          </div>

          <!-- Het buoi nhung CON han: khong duoc goi la "het han" -->
          <div v-if="usedUpCards.length" class="flex flex-col gap-2">
            <button
              class="flex items-center gap-1 text-left hover:underline"
              :class="[sectionClass, focusRing]"
              :aria-expanded="showUsedCards"
              @click="showUsedCards = !showUsedCards"
            >
              <i
                class="i-lucide-chevron-right transition-transform shrink-0"
                :class="{ 'rotate-90': showUsedCards }"
              />
              {{ $t('BONBOZ.CRM.CARDS_USED_UP', { n: usedUpCards.length }) }}
            </button>
            <p v-if="showUsedCards" class="text-xs text-n-slate-11 pl-3">
              {{ usedUpCards.map(cardName).join(', ') }}
            </p>
          </div>

          <div v-if="expiredCards.length" class="flex flex-col gap-2">
            <button
              class="flex items-center gap-1 text-left hover:underline"
              :class="[sectionClass, focusRing]"
              :aria-expanded="showExpiredCards"
              @click="showExpiredCards = !showExpiredCards"
            >
              <i
                class="i-lucide-chevron-right transition-transform shrink-0"
                :class="{ 'rotate-90': showExpiredCards }"
              />
              {{ $t('BONBOZ.CRM.CARDS_EXPIRED', { n: expiredCards.length }) }}
            </button>
            <!-- v-if o BOC NGOAI, khong phai v-show tren tung dong: khach mau co 36
                 the het han, v-show van dung het 36 node trong DOM du dang gap. -->
            <div v-if="showExpiredCards" class="flex flex-col gap-1">
              <p
                v-for="(k, i) in expiredCards"
                :key="i"
                class="text-xs text-n-slate-11 pl-3 flex justify-between gap-2"
              >
                <span class="truncate" :title="k.ten_the">{{
                  cardName(k)
                }}</span>
                <span class="shrink-0">
                  {{
                    $t('BONBOZ.CRM.CARD_EXPIRED_AT', {
                      ngay: fmtDate(k.het_han),
                    })
                  }}
                </span>
              </p>
            </div>
          </div>

          <!-- Epoint KHONG co API ghi: khong co nut tao lich hen / ghi chu o
                 day. Ghi chu va lich hen hien thi la ban DOC tu Epoint. -->
          <div
            v-if="linkedId"
            class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
          >
            <span class="truncate">
              <i class="i-lucide-book-open" />
              {{ $t('BONBOZ.CRM.READ_ONLY') }}
            </span>
            <button
              class="hover:text-n-ruby-11 hover:underline shrink-0 disabled:opacity-50"
              :class="focusRing"
              :disabled="saving"
              @click="saveLink(null)"
            >
              {{ $t('BONBOZ.CRM.UNLINK') }}
            </button>
          </div>
        </template>
      </div>
    </AccordionItem>
  </div>
</template>
